import { useEffect, useState } from "react";
import { fetchWrapper } from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import {
  getCmsSubscriptionRedirectPath,
  readCmsPlanId,
} from "src/helpers/coupon-client";
import { alertService } from "src/services";

interface Props {
  isOpen: boolean;
  onSuccess: () => void;
}

const CompleteProfileModal = ({ isOpen, onSuccess }: Props) => {
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [step, setStep] = useState<"email" | "mobile" | null>(null);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * After profile details are filled:
   * - CMS paid plan → continue to /subscription (Razorpay), do NOT free-trial
   * - CMS free trial (plan_id === 1) or no CMS intent → assign free trial
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const finishAfterProfileComplete = async (updatedLocalUser: any) => {
    const cmsPlanId = readCmsPlanId();
    const isCmsPaidPlan = Boolean(cmsPlanId) && Number(cmsPlanId) !== 1;

    if (isCmsPaidPlan) {
      onSuccess();
      window.location.href =
        getCmsSubscriptionRedirectPath() || "/subscription";
      return;
    }

    const hasPlan = updatedLocalUser.is_subscribed || updatedLocalUser.is_free;
    if (!hasPlan) {
      try {
        setIsLoading(true);
        const updatedProfileData = {
          free_plan: true,
          is_finished: true,
          demo_requested: "",
          skip_all: true,
        };
        const response = await fetchWrapper.put(
          "/api/profile/update",
          {
            profileData: {
              ...updatedProfileData,
              name: updatedLocalUser?.name || "User",
              email: updatedLocalUser?.email || "",
            },
          },
          { Authorization: `Bearer ${updatedLocalUser.token}` },
        );

        if (response.success) {
          localStorage.setItem(
            "BetaUser",
            JSON.stringify({
              ...updatedLocalUser,
              answers_submitted: true,
              is_subscribed: true,
              is_free: false,
              is_trial: response.data?.is_trial,
              plan: response.data?.plan,
              free_user_source_date: response.data?.free_user_source_date,
              next_working_day: response.data?.next_working_day,
            }),
          );
          if (response.data?.free_user_source_date) {
            sessionStorage.setItem(
              "selectedDate",
              response.data.free_user_source_date,
            );
          }
          sessionStorage.setItem("showFreeTrialStartedPopup", "true");
          onSuccess();
          window.location.href = "/dashboard";
          return;
        }
      } catch (e) {
        console.error("Error assigning free trial:", e);
      } finally {
        setIsLoading(false);
      }
    }
    onSuccess();
  };

  useEffect(() => {
    if (isOpen) {
      const user = getLocalStorageUser();
      // Private-relay Apple emails count as present — only ask for mobile.
      const hasEmail = Boolean(
        user?.email?.trim() || user?.customer_email?.trim(),
      );
      const isEmailMissing = !hasEmail;
      const isMobileMissing = !user?.phone_number?.trim();

      if (isEmailMissing) {
        setStep("email");
      } else if (isMobileMissing) {
        setStep("mobile");
      } else {
        setStep(null);
      }
      setEmail("");
      setMobileNumber("");
      setOtp("");
      setIsOTPSent(false);
    }
  }, [isOpen]);

  const handleGetEmailOTP = async () => {
    if (!email) {
      alertService.error("Email ID is mandatory.");
      return;
    }
    if (!emailRegex.test(email)) {
      alertService.error("Please enter a valid email address.");
      return;
    }

    try {
      setIsLoading(true);
      const user = getLocalStorageUser();
      const token = user.token;

      const res = await fetchWrapper.post(
        "/api/profile/send_otp",
        {
          email: email,
          name: user.name || "User",
        },
        {
          Authorization: `Bearer ${token}`,
        },
      );

      if (res.success) {
        setIsOTPSent(true);
        alertService.success("OTP sent successfully!");
      } else {
        alertService.error(res.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      alertService.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveMobile = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      alertService.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      setIsLoading(true);
      const user = getLocalStorageUser();
      const token = user.token;

      await fetchWrapper.post("/api/users/checkMobile", {
        mobile_number: `+91${mobileNumber}`,
        sendOtp: "true",
      });

      const response = await fetchWrapper.get(
        "/api/profile/retrieve",
        undefined,
        { Authorization: `Bearer ${token}` },
      );

      const profileData = {
        ...response.userData,
        phone_number: `+91${mobileNumber}`,
      };

      const updateResponse = await fetchWrapper.put(
        "/api/profile/update",
        { profileData },
        { Authorization: `Bearer ${token}` },
      );

      if (updateResponse.success) {
        const updatedLocalUser = {
          ...user,
          phone_number: profileData.phone_number,
        };
        localStorage.setItem("BetaUser", JSON.stringify(updatedLocalUser));

        const hasEmail = Boolean(
          updatedLocalUser.email?.trim() ||
          updatedLocalUser.customer_email?.trim(),
        );

        if (!hasEmail) {
          setStep("email");
          setEmail("");
          setOtp("");
          setIsOTPSent(false);
          alertService.success(
            "Mobile number verified successfully! Please provide your Email.",
          );
        } else {
          // Private-relay Apple login already has email — mobile is enough;
          // API/profile update handles the rest. Close via onSuccess recheck.
          alertService.success("Profile updated successfully!");
          finishAfterProfileComplete(updatedLocalUser);
        }
      } else {
        alertService.error(
          updateResponse.message || "Failed to update profile.",
        );
      }
    } catch (e) {
      const error = e as Error;
      console.error("Error updating profile:", error);
      const message = error?.message || error || "Failed to update profile.";
      alertService.error(
        typeof message === "string" ? message : "Failed to update profile.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailAndSave = async () => {
    if (!otp) {
      alertService.error("OTP is required.");
      return;
    }

    try {
      setIsLoading(true);
      const user = getLocalStorageUser();
      const token = user.token;

      const response = await fetchWrapper.get(
        "/api/profile/retrieve",
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      if (!response.userData) {
        throw new Error("Failed to retrieve profile data.");
      }

      const profileData = {
        ...response.userData,
        email:
          response.userData.email &&
          !response.userData.email.endsWith("@privaterelay.appleid.com")
            ? response.userData.email
            : email,
        customer_email: email,
        verification_otp: otp,
      };

      const updateResponse = await fetchWrapper.put(
        "/api/profile/update",
        { profileData },
        { Authorization: `Bearer ${token}` },
      );

      if (updateResponse.success) {
        const updatedLocalUser = {
          ...user,
          email: profileData.email,
          customer_email: profileData.customer_email,
        };
        localStorage.setItem("BetaUser", JSON.stringify(updatedLocalUser));

        const isMobileMissing = !updatedLocalUser.phone_number?.trim();
        if (isMobileMissing) {
          setStep("mobile");
          setOtp("");
          setIsOTPSent(false);
          alertService.success(
            "Email verified. Please verify your Mobile number.",
          );
        } else {
          alertService.success("Profile updated and email verified.");
          finishAfterProfileComplete(updatedLocalUser);
        }
      } else {
        alertService.error(
          updateResponse.message || "Failed to update profile.",
        );
      }
    } catch (e) {
      const error = e as Error;
      console.error("Error updating profile:", error);
      const message = error?.message || error || "Failed to verify OTP.";
      alertService.error(
        typeof message === "string"
          ? message
          : "Invalid OTP or Failed to update.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !step) return null;

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            Please complete your Profile
          </h2>
          <p className="text-gray-400">
            To continue using ABillion, please provide a valid{" "}
            {step === "email" ? "Email ID" : "Mobile number"}.
          </p>
        </div>

        <div className="space-y-6">
          {step === "email" ? (
            !isOTPSent ? (
              <div>
                <label
                  htmlFor="modal-email"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Email ID <span className="text-red-500">*</span>
                </label>
                <input
                  id="modal-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-white outline-none transition-all"
                  placeholder="name@example.com"
                  required
                />
                {emailRegex.test(email) && (
                  <button
                    type="button"
                    onClick={handleGetEmailOTP}
                    disabled={isLoading}
                    className="cursor-pointer mt-6 w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-4 rounded-xl transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Sending..." : "Get OTP"}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-400">
                  OTP sent to{" "}
                  <span className="text-white font-medium">{email}</span>
                  <button
                    onClick={() => setIsOTPSent(false)}
                    className="ml-2 text-cyan-400 hover:underline"
                  >
                    Change
                  </button>
                </div>
                <div>
                  <label
                    htmlFor="modal-otp"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    OTP <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="modal-otp"
                    type="text"
                    value={otp}
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-white outline-none transition-all tracking-widest text-center text-xl font-bold"
                    placeholder="000000"
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setOtp(value);
                    }}
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={handleVerifyEmailAndSave}
                  disabled={isLoading}
                  className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-xl transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Verifying..." : "Verify & Continue"}
                </button>
              </div>
            )
          ) : (
            <div>
              <label
                htmlFor="modal-mobile"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Mobile number <span className="text-red-500">*</span>
              </label>
              <input
                id="modal-mobile"
                type="tel"
                value={mobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 10) setMobileNumber(value);
                }}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-white outline-none transition-all"
                placeholder="Mobile number"
                maxLength={10}
                required
              />
              {mobileNumber.length === 10 && (
                <button
                  type="button"
                  onClick={handleSaveMobile}
                  disabled={isLoading}
                  className="cursor-pointer mt-6 w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-xl transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Saving..." : "Save & Continue"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompleteProfileModal;
